import { messages } from "../i18n/index";
import { button } from "./dom";
import { MENU_TABS, type MenuTab, type Store, type ViewState } from "./store";
import { mountTabs } from "./tabs";

/** Below this width the panel is a bottom sheet; keep in sync with style.css. */
const PANEL_SHEET_QUERY = "(max-width: 600px)";

export interface Menu {
  /** Empty tab panels for the tab modules to fill. */
  panels: Record<MenuTab, HTMLElement>;
  focusActiveTab(): void;
  /** Read at the moment of use so a resize across the breakpoint is honoured. */
  isSheet(): boolean;
}

/** `toggleHost` receives the ☰ button; it sits in the time pill, right of the date. */
export function mountMenu(container: HTMLElement, toggleHost: HTMLElement, store: Store): Menu {
  const root = document.createElement("div");
  root.className = "menu";
  const scrim = document.createElement("div");
  scrim.className = "menu-scrim";
  scrim.addEventListener("click", () => store.set({ menuOpen: false }));

  const panel = document.createElement("section");
  panel.id = "menu-panel";
  panel.className = "menu-panel glass";

  const tabs = mountTabs(MENU_TABS, "menu", (tab) => store.set({ menuTab: tab }));

  const handle = document.createElement("div");
  handle.className = "menu-handle";
  const close = button("✕", () => store.set({ menuOpen: false }), "menu-close");
  const header = document.createElement("div");
  header.className = "menu-header";
  header.append(tabs.tablist, close);
  panel.append(handle, header, ...MENU_TABS.map((tab) => tabs.panels[tab]));

  const toggle = button("☰", () => store.set({ menuOpen: !store.state.menuOpen }), "menu-button");
  toggle.setAttribute("aria-controls", panel.id);

  // The window shortcut handler skips keys aimed at buttons and inputs,
  // so the menu handles Esc and M itself while focus is inside it or on its toggle.
  const onKey = (e: KeyboardEvent): void => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const typing = e.target instanceof HTMLInputElement && e.target.type === "search";
    if (e.key === "Escape" && store.state.menuOpen) store.set({ menuOpen: false });
    else if (!typing && (e.key === "m" || e.key === "M"))
      store.set({ menuOpen: !store.state.menuOpen });
    else return;
    e.preventDefault();
  };
  root.addEventListener("keydown", onKey);
  toggle.addEventListener("keydown", onKey);

  root.append(scrim, panel);
  container.append(root);
  toggleHost.append(toggle);

  const render = (s: ViewState): void => {
    const m = messages(s.lang).menu;
    panel.setAttribute("aria-label", m.label);
    close.setAttribute("aria-label", m.closePanel);
    // blur before hiding so global shortcuts work again (a focused button would swallow them)
    const active = document.activeElement;
    const inMenu = active === toggle || root.contains(active);
    if (!s.menuOpen && active instanceof HTMLElement && inMenu) active.blur();
    panel.hidden = !s.menuOpen;
    scrim.hidden = !s.menuOpen;
    toggle.textContent = s.menuOpen ? "✕" : "☰";
    toggle.setAttribute("aria-expanded", String(s.menuOpen));
    toggle.setAttribute("aria-label", s.menuOpen ? m.close : m.open);
    tabs.sync(s.menuTab, m.tabs);
  };
  store.subscribe(render);
  render(store.state);

  return {
    panels: tabs.panels,
    focusActiveTab: () => tabs.focus(store.state.menuTab),
    isSheet: () => matchMedia(PANEL_SHEET_QUERY).matches,
  };
}
